import React, { useState, useEffect } from 'react';
import { API_URL as API_URL_CONFIG } from '../../config';
import {
    IoAddOutline, IoSaveOutline, IoClose, IoCheckboxOutline, IoSquareOutline,
    IoDocumentTextOutline, IoPersonOutline, IoSearchOutline, IoCheckmarkCircle,
    IoCloseCircle, IoTimeOutline, IoAlertCircleOutline, IoChevronBack
} from 'react-icons/io5';
import './onboarding.css';

const API_URL = API_URL_CONFIG;

const HRDocumentAssignment = ({ onBack }) => {
    const [users, setUsers] = useState([]);
    const [masterDocs, setMasterDocs] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedDocIds, setSelectedDocIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const [searchTerm, setSearchTerm] = useState('');

    const getAuthHeaders = () => ({
        'Authorization': `Bearer ${localStorage.getItem('zTaskAuthToken') || ''}`,
        'Content-Type': 'application/json'
    });

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            // Fetch users with document status
            const usersRes = await fetch(`${API_URL}/hr-documents/users/document-status`, {
                headers: getAuthHeaders()
            });
            const usersData = await usersRes.json();

            // Fetch master documents
            const docsRes = await fetch(`${API_URL}/hr-documents/master-documents`, {
                headers: getAuthHeaders()
            });
            const docsData = await docsRes.json();

            if (usersRes.ok) setUsers(usersData.users || []);
            if (docsRes.ok) setMasterDocs(docsData.documents || []);

        } catch (error) {
            console.error('Error fetching data:', error);
            showToast('Failed to load data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUserSelect = async (user) => {
        setSelectedUser(user);
        setSelectedDocIds([]);

        // Fetch current document assignments for this user
        try {
            const res = await fetch(`${API_URL}/hr-documents/user/${user.id}/required-documents`, {
                headers: getAuthHeaders()
            });
            const data = await res.json();

            if (res.ok) {
                // Pre-select already assigned documents
                const assignedIds = data.documents.map(doc => doc.document_id);
                setSelectedDocIds(assignedIds);
            }
        } catch (error) {
            console.error('Error fetching user documents:', error);
        }
    };

    const toggleDocument = (docId) => {
        if (selectedDocIds.includes(docId)) {
            setSelectedDocIds(selectedDocIds.filter(id => id !== docId));
        } else {
            setSelectedDocIds([...selectedDocIds, docId]);
        }
    };

    const handleSave = async () => {
        if (!selectedUser) {
            showToast('Please select a user first', 'error');
            return;
        }

        if (selectedDocIds.length === 0) {
            showToast('Please select at least one document', 'error');
            return;
        }

        setSaving(true);
        try {
            const currentUserId = JSON.parse(atob(localStorage.getItem('zTaskAuthToken').split('.')[1])).userId;

            const res = await fetch(`${API_URL}/hr-documents/assign-documents`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    userId: selectedUser.id,
                    documentIds: selectedDocIds,
                    assignedBy: currentUserId
                })
            });

            const data = await res.json();

            if (res.ok) {
                showToast(data.message || 'Documents assigned successfully!', 'success');
                fetchInitialData(); // Refresh user list
                setSelectedUser(null);
                setSelectedDocIds([]);
            } else {
                showToast(data.error || 'Failed to assign documents', 'error');
            }
        } catch (error) {
            console.error('Error assigning documents:', error);
            showToast('Network error occurred', 'error');
        } finally {
            setSaving(false);
        }
    };

    const showToast = (message, type) => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 4000);
    };

    const getStatusBadge = (status) => {
        const classes = {
            'Complete': 'status-badge active',
            'In Progress': 'status-badge onboarding',
            'Pending': 'status-badge',
            'Not Assigned': 'status-badge'
        };
        return <span className={classes[status] || 'status-badge'}>{status}</span>;
    };

    const getStatusIcon = (status) => {
        if (status === 'Complete') return <IoCheckmarkCircle size={20} color="#10B981" />;
        if (status === 'In Progress') return <IoTimeOutline size={20} color="#F59E0B" />;
        if (status === 'Pending') return <IoCloseCircle size={20} color="#EF4444" />;
        return <IoAlertCircleOutline size={20} color="#94A3B8" />;
    };

    const filteredUsers = users.filter(user =>
        !searchTerm ||
        user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.emp_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="onboarding-page">
                <div className="portal-loading-screen">
                    <div className="loader"></div>
                    Loading Document Management...
                </div>
            </div>
        );
    }

    return (
        <div className="onboarding-page">
            {toast.show && (
                <div className={`toast-notification show ${toast.type}`}>
                    <div className="toast-content">
                        <div className="toast-title">{toast.type === 'success' ? 'Success' : 'Error'}</div>
                        <div className="toast-msg">{toast.message}</div>
                    </div>
                    <IoClose className="toast-close-btn" onClick={() => setToast({ show: false })} />
                </div>
            )}

            {/* Assignment Modal */}
            {selectedUser && (
                <div className="form-overlay">
                    <div className="form-card" style={{ maxWidth: '700px' }}>
                        <div className="form-header-modal">
                            <h3>Assign Documents: {selectedUser.full_name}</h3>
                            <button className="btn-icon" onClick={() => setSelectedUser(null)}>
                                <IoClose size={24} />
                            </button>
                        </div>

                        <div className="form-body-modal" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <div style={{ marginBottom: '20px', padding: '12px', background: '#F8FAFC', borderRadius: '8px' }}>
                                <div style={{ fontSize: '13px', color: '#64748B' }}>
                                    <strong>Employee:</strong> {selectedUser.emp_code} - {selectedUser.email}
                                </div>
                                <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '4px' }}>
                                    Current Status: {selectedUser.uploaded_count}/{selectedUser.total_required} documents uploaded
                                </div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#1E293B', marginBottom: '12px' }}>
                                    Select Documents to Assign:
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {masterDocs.map(doc => (
                                        <div
                                            key={doc.id}
                                            onClick={() => toggleDocument(doc.id)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '12px',
                                                padding: '12px',
                                                background: selectedDocIds.includes(doc.id) ? '#EEF2FF' : '#FFFFFF',
                                                border: `1px solid ${selectedDocIds.includes(doc.id) ? '#4F46E5' : '#E2E8F0'}`,
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <div style={{ marginTop: '2px' }}>
                                                {selectedDocIds.includes(doc.id) ?
                                                    <IoCheckboxOutline size={20} color="#4F46E5" /> :
                                                    <IoSquareOutline size={20} color="#94A3B8" />
                                                }
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: '600', fontSize: '13px', color: '#1E293B' }}>
                                                    {doc.document_name}
                                                    {doc.is_mandatory && <span style={{ color: '#EF4444', marginLeft: '4px' }}>*</span>}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>
                                                    {doc.description || 'No description'}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={{ fontSize: '12px', color: '#64748B', padding: '10px', background: '#FEF3C7', borderRadius: '6px', border: '1px solid #FDE68A' }}>
                                <strong>Note:</strong> Selected documents ({selectedDocIds.length}) will be assigned to this employee.
                                They will receive a notification to upload these documents.
                            </div>
                        </div>

                        <div className="form-footer-modal">
                            <button className="btn-secondary" onClick={() => setSelectedUser(null)}>
                                Cancel
                            </button>
                            <button
                                className="btn-primary-add"
                                onClick={handleSave}
                                disabled={saving || selectedDocIds.length === 0}
                            >
                                {saving ? 'Assigning...' : `Assign ${selectedDocIds.length} Document(s)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="onboarding-header">
                <div className="header-content">
                    {onBack && (
                        <button className="btn-icon-back" onClick={onBack} style={{ marginRight: '12px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <IoChevronBack size={24} color="#1E293B" />
                        </button>
                    )}
                    <h1>Document Assignment</h1>
                </div>
            </div>

            <div className="content-wrapper" style={{ padding: '24px 40px' }}>
                <div className="list-section" style={{ flex: 1 }}>
                    <div className="search-bar-container">
                        <IoSearchOutline className="search-icon" />
                        <input
                            className="search-input"
                            placeholder="Search by name, emp code, or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div style={{
                        background: '#FFFFFF',
                        borderRadius: '16px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '80px 2fr 1.5fr 100px 100px 100px',
                            gap: '16px',
                            padding: '16px 24px',
                            background: '#F8FAFC',
                            borderBottom: '1px solid #E2E8F0',
                            fontSize: '11px',
                            fontWeight: '800',
                            color: '#64748B',
                            textTransform: 'uppercase'
                        }}>
                            <div>Code</div>
                            <div>Employee</div>
                            <div>Email</div>
                            <div style={{ textAlign: 'center' }}>Required</div>
                            <div style={{ textAlign: 'center' }}>Uploaded</div>
                            <div style={{ textAlign: 'center' }}>Action</div>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {filteredUsers.length > 0 ? filteredUsers.map(user => (
                                <div
                                    key={user.id}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '80px 2fr 1.5fr 100px 100px 100px',
                                        gap: '16px',
                                        padding: '16px 24px',
                                        borderBottom: '1px solid #F1F5F9',
                                        alignItems: 'center',
                                        transition: 'background 0.2s',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#F8FAFC'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                    <div style={{ fontSize: '13px', color: '#64748B', fontWeight: '600' }}>
                                        {user.emp_code || 'N/A'}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: '700', fontSize: '14px', color: '#1E293B' }}>
                                            {user.full_name}
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#64748B' }}>
                                        {user.email}
                                    </div>
                                    <div style={{ textAlign: 'center', fontSize: '16px', fontWeight: '700', color: '#4F46E5' }}>
                                        {user.total_required}
                                    </div>
                                    <div style={{
                                        textAlign: 'center',
                                        fontSize: '16px',
                                        fontWeight: '700',
                                        color: user.uploaded_count === user.total_required && user.total_required > 0 ? '#10B981' : '#64748B'
                                    }}>
                                        {user.uploaded_count}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                        {getStatusIcon(user.status)}
                                        <button
                                            className="btn-secondary"
                                            onClick={() => handleUserSelect(user)}
                                            style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                height: 'auto',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                        >
                                            <IoDocumentTextOutline size={14} />
                                            Assign
                                        </button>
                                    </div>
                                </div>
                            )) : (
                                <div style={{ padding: '60px 20px', textAlign: 'center', color: '#94A3B8' }}>
                                    <IoPersonOutline size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
                                    <div>No employees found</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HRDocumentAssignment;
